import { Injectable, NgZone } from '@angular/core';
import { FormSummary } from '../interfaces/form-summary';
import { DispatchFormSummary } from '../interfaces/dispatch-form-summary';
import { TokenService } from './token-service';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { BackendService } from './backend-service';
import { Observable } from 'rxjs/internal/Observable';
import { SavedComment } from '../interfaces/saved-comment';
import { UnitData } from '../interfaces/unit-data';
import { StaffData } from '../interfaces/staff-data';
import { InsertFlowsSelectList } from '../interfaces/insert-flows-select-list';
import { PersonalLexiconData } from '../interfaces/personal-lexicon-data';
import { StaffStampData } from '../interfaces/staff-stamp-data';
import { RequestResult } from '../interfaces/request-result';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private formSummariesSubject: BehaviorSubject<FormSummary[]>;
  private dispatchFormSummariesSubject: BehaviorSubject<DispatchFormSummary[]>;

  constructor(
    private tokenService: TokenService,
    private backendService: BackendService,
    private zone: NgZone
  ) {
    this.formSummariesSubject = new BehaviorSubject<FormSummary[]>([]);
    this.dispatchFormSummariesSubject = new BehaviorSubject<
      DispatchFormSummary[]
    >([]);
  }

  getLoginUserData(): Observable<any> {
    return this.backendService.get('api/LoginUserData');
  }

  getFormSummaries(): BehaviorSubject<FormSummary[]> {
    this.refreshFormSummaries();
    return this.formSummariesSubject;
  }

  refreshFormSummaries() {
    console.log('Calling refreshFormSummaries');
    this.backendService
      .get<FormSummary[]>('api/FormSummaries')
      .subscribe((data: FormSummary[] | null) => {
        console.log('FormSummaries received:', data);
        const summaries = data ?? []; // 若為 null 則設為空陣列
        setTimeout(() => {
          this.zone.run(() => {
            console.log('Updating formSummaries with:', summaries);
            this.formSummariesSubject.next(summaries);
            console.log('FormSummaries updated in subject');
          });
        }, 0);
      });
  }

  getDispatchFormSummaries(): BehaviorSubject<DispatchFormSummary[]> {
    this.refreshDispatchFormSummaries();
    return this.dispatchFormSummariesSubject;
  }

  refreshDispatchFormSummaries() {
    console.log('Calling refreshDispatchFormSummaries');
    this.backendService
      .get<DispatchFormSummary[]>('api/DispatchFormSummaries')
      .subscribe((data: DispatchFormSummary[] | null) => {
        console.log('DispatchFormSummaries received:', data);
        const summaries = data ?? []; // 若為 null 則設為空陣列
        setTimeout(() => {
          this.zone.run(() => {
            console.log('Updating dispatchFormSummaries with:', summaries);
            this.dispatchFormSummariesSubject.next(summaries);
            console.log('DispatchFormSummaries updated in subject');
          });
        }, 0);
      });
  }

  getSavedComments(): Observable<SavedComment[] | null> {
    return this.backendService.get<SavedComment[]>('api/SavedComments');
  }

  getToDispatchLevel1Units(unitCode: string): Observable<UnitData[] | null> {
    return this.backendService.get<UnitData[]>(
      `api/ToDispatchLevel1Units/?utCod=${unitCode}`
    );
  }

  getToDispatchLevel2Units(
    upUnitCode: string,
    utCod: string
  ): Observable<UnitData[] | null> {
    return this.backendService.get<UnitData[]>(
      `api/ToDispatchLevel2Units/?upUtCod=${upUnitCode}&utCod=${utCod}`
    );
  }

  getToDispatchStaffsOfUnit(unitCode: string): Observable<StaffData[] | null> {
    return this.backendService.get<StaffData[]>(
      `api/ToDispatchStaffsOfUnit/?utCod=${unitCode}`
    );
  }

  getUpUnit(utCod: string): Observable<string | null> {
    return this.backendService.get(`api/UpUnit/?utCod=${utCod}`);
  }

  getLevel1Units(): Observable<UnitData[] | null> {
    return this.backendService.get('api/Level1Units');
  }

  getLevel2Units(utCod: string): Observable<UnitData[] | null> {
    return this.backendService.get(`api/Level2Units/?utCod=${utCod}`);
  }

  getStaffsOfUnit(utCod: string): Observable<StaffData[] | null> {
    return this.backendService.get(`api/StaffsOfUnit/?utCod=${utCod}`);
  }

  getFlowSelectList(formId: string): Observable<InsertFlowsSelectList[] | null> {
    return this.backendService.get(
      `api/Timeline/FlowSelectList/?formId=${formId}`
    );
  }

  getPersonalLexicon(): Observable<PersonalLexiconData[] | null> {
    return this.backendService.get('api/PersonalPhase');
  }

  addLexicon(lexiconText: string): Observable<boolean | null> {
    return this.backendService.post('api/PersonalPhase', { Text: lexiconText });
  }

  updateLexicon(lexiconId: number, lexiconText: string): Observable<RequestResult<boolean | null>> {
    return this.backendService.patch<RequestResult<boolean | null>>('api/PersonalPhase', {
      Id: lexiconId,
      Text: lexiconText
    });
  }

  deleteLexicon(lexiconId: number): Observable<boolean | null> {
    return this.backendService.delete(`api/PersonalPhase/?id=${lexiconId}`);
  }

  getMyUnitsOfForm(formId: string): Observable<string[] | null> {
    return this.backendService.get(`api/MyUnitsOfForm/?formId=${formId}`);
  }

  getStaffPositionData(utCod: string): Observable<StaffStampData | null> {
    return this.backendService.get(`api/MyPositionData/?utCod=${utCod}`);
  }
}
